import { useApplicationContext } from "src/hooks/useApplication";
import { useEntityContext } from "../../useEntityContext";
import { EntityTypes } from "@vzbl/integrations";
import { Card, Col, Row, Typography, Tooltip, Empty } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { HeaderPanel } from "./layout/HeaderPanel";
import { toHeaderAttributes } from "./HeaderAttributes";
import { SummaryPanel } from "./layout/SummaryPanel";
import { useEntityHistory } from "../../useEntityHistory";
import { useOrg } from "src/hooks/useOrg";
import { useEffect, useMemo } from "react";
import { EntityTimeline } from "src/components/progress/EntityTimeline";
import { IntegrationIcon } from "src/components/IntegrationIcon";
import { EntityContributors } from "./EntityContributors";
import { DynamicProperties } from "./DynamicProperties";
import { StandardCard } from "./StandardCard";
import { theme } from "antd";
import { MetricsDisplay } from "./MetricsDisplay";
import {
  StageProgress,
  StatusHistoryItem,
  generateStatusHistoryFromEvents,
  normalizeEvents,
} from "src/components/progress";
import { useEntitiesList } from "src/search/lib/useEntitiesList";
import { serializeParams } from "src/search/lib/params-state-reducer";
import { Contributors } from "../../Contributors";
import { issueDisplayRules, hiddenIssueFields } from "./EntityDisplayRules";

const { Title } = Typography;

/**
 * Props interface for IssueOverview component
 */
interface IssueOverviewProps {
  entity: any;
  statusHistory?: StatusHistoryItem[];
  entityHistory?: any;
}

/**
 * Helper function to extract readable value from property
 * Handles various data types and formats them for display
 */
const getReadableValue = (value: any): string => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return "N/A";
  }

  // Handle arrays
  if (Array.isArray(value)) {
    // If array of objects, try to extract meaningful values
    if (value.length > 0 && typeof value[0] === "object") {
      return value
        .map((item) => {
          if (item.title) return item.title;
          if (item.name) return item.name;
          if (item.value) return item.value;
          if (item.id) return item.id;
          return getReadableValue(item);
        })
        .join(", ");
    }
    // Simple array
    return value.join(", ");
  }

  // Handle objects
  if (typeof value === "object") {
    // Try common object properties in order of preference
    const commonProps = ["title", "name", "value", "label", "key", "id"];
    for (const prop of commonProps) {
      if (value[prop] !== undefined) {
        return value[prop].toString();
      }
    }
    // If no common properties found, try to stringify
    try {
      const strValue = JSON.stringify(value);
      return strValue === "{}" ? "N/A" : strValue;
    } catch {
      return "N/A";
    }
  }

  // Handle basic types
  return value.toString();
};

export const IssueOverview: React.FC<IssueOverviewProps> = ({
  entity,
  statusHistory = [],
}) => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();

  const { uuid } = entity.properties;
  const entityType = entity.labels[0];

  // --------------------------------------
  // get entity history endpoint
  //----------------------------------------
  // Get entity history for status changes
  const entityHistory = useEntityHistory({
    orgId: useOrg().id!,
    entityUuid: uuid,
    type: entityType,
  });

  // Transform this is for the stage process component
  const entityStatusHistory = useMemo(() => {
    if (entityHistory.query.isLoading || !entityHistory.query.data?.data) {
      return [];
    }

    const events = normalizeEvents(entityHistory.query.data.data);
    return generateStatusHistoryFromEvents(events, entity);
  }, [entityHistory.query.data, entityHistory.query.isLoading, entity]);

  //----------------------------------------
  // get entity events endpoint
  //get all the events for the entity*/
  // Base search state
  //----------------------------------------
  const searchState = useMemo(
    () => ({
      orgId: orgId,
      filters: entity
        ? [
            {
              key: "Event.Issue.uuid",
              target: "Issue",
              attribute: "uuid",
              comparison: { IN: [uuid] },
            },
          ]
        : [],
      select: [EntityTypes.Event],
      timezone: "America/New_York",
      sort: [
        {
          target: "Event",
          attribute: "occurredAt",
          direction: "DESC",
        },
      ],
      pageSize: 1000,
    }),
    [orgId, entity, uuid]
  );

  const serialized = useMemo(() => serializeParams(searchState), [searchState]);
  const activities = useEntitiesList({ state: searchState, serialized });

  // Subscribe to data updates
  useEffect(() => {
    activities.subscribe();

    return () => activities.unsubscribe();
  }, [activities]);

  //Todo clean this up and make it more efficient, figure out paging, and get the rest of the events
  const normalizedEvents = normalizeEvents(activities.data?.data ?? []);

  return (
    <div>
      <Content
        entity={entity}
        statusHistory={entityStatusHistory}
        entityHistory={{
          isLoading: activities.isLoading,
          data: normalizedEvents,
          error: activities.error,
        }}
      />
    </div>
  );
};

const Content: React.FC<IssueOverviewProps> = ({
  entity,
  statusHistory = [],
  entityHistory,
}) => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const { token } = theme.useToken();

  const {
    issueKey,
    issueType,
    priority,
    status,
    integration,
    title,
    description,
    vzblSummary,
  } = entity.properties;

  const handlePropertyClick = (key: string, value: any) => {
    console.log("Property clicked:", { key, value });
  };

  return (
    <div style={{ padding: "24px", paddingBottom: "48px" }}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <HeaderPanel
            entity={entity}
            attributes={toHeaderAttributes({
              orgId,
              context,
              entityType: entity.labels[0],
              attributes: [
                { name: "issueType", text: issueType },
                { name: "priority", text: priority },
                { name: "status", text: status },
              ],
            })}
            icon={<IntegrationIcon source={integration} size="large" />}
            label={issueKey}
          />
        </Col>
      </Row>

      <Row
        gutter={[24, 24]}
        style={{ height: "550px", overflow: "hidden", marginBottom: "24px" }}
      >
        <Col span={16}>
          <SummaryPanel
            title={title}
            description={description}
            aiSummary={vzblSummary}
          />
        </Col>
        <Col span={8}>
          <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
            Properties
          </Title>
          <StandardCard
            style={{ height: "500px", padding: "0px 12px" }}
            headerStyle={{ display: "none" }}
          >
            <DynamicProperties
              properties={entity.properties}
              orgId={orgId}
              context={context}
              entityType={entity.labels[0] as keyof typeof EntityTypes}
              maxInitialProperties={9}
              showGroupHeaders={true}
              showMoreButton={true}
              layout="split"
              displayRules={issueDisplayRules}
              hiddenFields={hiddenIssueFields}
              onPropertyClick={handlePropertyClick}
            />
          </StandardCard>
        </Col>
      </Row>

      <Row
        gutter={[24, 24]}
        style={{
          height: "800px",
          marginBottom: "48px",
          marginLeft: "0px",
          marginRight: "0px",
        }}
      >
        <Col
          span={12}
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
              Progress
            </Title>

            {/* Fixed Height for Progress Card */}
            <StandardCard
              title="Progress"
              headerStyle={{ display: "none" }}
              bodyStyle={{ padding: "12px" }}
              style={{ height: "100px", marginBottom: 24, flexShrink: 0 }}
              variant="grey"
            >
              <StageProgress
                currentStatus={status}
                statusHistory={statusHistory}
                showTimestamps
                showDuration
                style={{ minHeight: "0" }}
              />
            </StandardCard>

            {/* Flexible Timeline Card */}
            <StandardCard
              title="Timeline"
              headerStyle={{ display: "none" }}
              bodyStyle={{
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
              style={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              {entityHistory.isLoading ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <LoadingOutlined />
                </div>
              ) : entityHistory.error ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  Failed to load timeline
                </div>
              ) : entityHistory.data?.length ? (
                <EntityTimeline
                  history={entityHistory.data}
                  style={{
                    flex: 1,
                    overflow: "auto",
                    padding: "0px 12px 12px 0px",
                  }}
                />
              ) : (
                <Empty description="No history available" />
              )}
            </StandardCard>
          </div>
        </Col>

        <Col
          span={12}
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{ marginBottom: "24px", flexShrink: 0 }}>
              <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
                Metrics
              </Title>
              <MetricsDisplay
                entity={entity}
                maxMetrics={3}
                config={{
                  hiddenMetrics: [
                    "vzblClarityScore",
                    "vzblFrictionScore",
                    "vzblClassification",
                    "vzblUrgency",
                  ],
                }}
                style={{ height: "100px" }}
              />
            </div>

            {/* Flexible Contributors Container */}
            <Contributors>
              <EntityContributors
                style={{ flex: 1, display: "flex", flexDirection: "column" }}
                entity={entity}
                showTitle={false}
              />
            </Contributors>
          </div>
        </Col>
      </Row>
    </div>
  );
};
